import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { cardEnter } from './motion'

interface CardProps {
  children: ReactNode
  className?: string
  /** Opts into the staggered entrance. Off for content that must not move on refresh. */
  animate?: boolean
  as?: 'div' | 'section' | 'article'
  /** Four milled rivets at the corners. Chrome only — never behind a table of figures. */
  rivets?: boolean
  /** Lifts further on hover. For a housing that is itself a link or a button. */
  interactive?: boolean
}

/**
 * A machine housing: white glass, a hairline edge, and real elevation.
 *
 * ── Why this is a shadow now ──
 * The dark theme's rule was that elevation comes from a lighter surface plus a 1px line and
 * never from a drop shadow, because heavy shadows on near-black read as smudges. On the
 * Studio canvas that reverses: every surface is already near-white, so "lighter surface"
 * conveys nothing and `--lift` is the only cue available. Same intent, opposite mechanism.
 *
 * ── The entrance, which must stay as it is ──
 * The entrance animation is SELF-DRIVEN (`initial`/`animate` set here), never inherited from
 * a parent's stagger orchestration. A card whose data or lazy chunk resolves after the
 * parent finished animating would otherwise mount into the parent's completed "animate"
 * state and stay pinned at opacity 0 — invisible, with its content present in the DOM and no
 * error anywhere. Self-driving costs the cross-card stagger and buys a card that is always
 * visible.
 */
export function Housing({
  children,
  className,
  animate = true,
  as = 'div',
  rivets = false,
  interactive = false,
}: CardProps) {
  const Component = motion[as]

  return (
    <Component
      variants={cardEnter}
      initial={animate ? 'initial' : false}
      animate="animate"
      className={cn(
        'surface-glass lift relative rounded-[18px] ring-1 ring-line',
        interactive && 'lift-hover transition-shadow duration-200',
        className,
      )}
    >
      {rivets && <Rivets />}
      {children}
    </Component>
  )
}

/**
 * Kept as an alias so pages not yet converted keep rendering. New code should say
 * `Housing` — the name is the one thing carrying the metaphor into the call site.
 */
export const Card = Housing

/** Four corner rivets. Purely decorative, so hidden from assistive technology. */
function Rivets() {
  return (
    <span aria-hidden>
      {(
        [
          'left-2.5 top-2.5',
          'right-2.5 top-2.5',
          'left-2.5 bottom-2.5',
          'right-2.5 bottom-2.5',
        ] as const
      ).map((pos) => (
        <span
          key={pos}
          // 8px, not 6: at 6 they read as dust specks on the glass rather than as hardware.
          className={cn('absolute size-2 rounded-full', pos)}
          style={{ background: 'var(--rivet)', boxShadow: 'var(--rivet-shadow)' }}
        />
      ))}
    </span>
  )
}

export function CardHeader({
  title,
  subtitle,
  action,
  className,
}: {
  title: ReactNode
  subtitle?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4 p-5 pb-0', className)}>
      <div className="min-w-0">
        <h2 className="display text-lg text-ink">{title}</h2>
        {subtitle && <p className="mt-1.5 text-xs text-muted">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

export function CardBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('p-5', className)}>{children}</div>
}

export function CardFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('border-t border-line px-5 py-3 text-[10px] text-faint', className)}>
      {children}
    </div>
  )
}
